import type { I18nKeys } from '@/shared/core/hooks/use-translation/use-translation.ts'

export interface ExperienceTech {
	category: 'frontend' | 'backend' | 'data' | 'infra' | 'mobile' | 'migration'
	name: string
}

export interface ExperienceData {
	badgeKey?: I18nKeys
	company: string
	descriptionKey: I18nKeys
	endDate: Date | null // null means "Present"
	externalLink: string
	highlightKeys: I18nKeys[]
	id: string
	location: string
	periodKey: I18nKeys
	projectKey: I18nKeys
	roleKey: I18nKeys
	startDate: Date
	tech: ExperienceTech[]
}

/**
 * Calculate the duration between two dates in years and months
 */
export function calculateDuration(
	startDate: Date,
	endDate: Date | null,
): { months: number; years: number } {
	const end = endDate ?? new Date()
	const totalMonths =
		(end.getFullYear() - startDate.getFullYear()) * 12 +
		(end.getMonth() - startDate.getMonth())

	const years = Math.floor(totalMonths / 12)
	const months = totalMonths % 12

	return { months, years }
}

/**
 * Format duration as "Xy Ym" string
 */
export function formatDuration(startDate: Date, endDate: Date | null): string {
	const { years, months } = calculateDuration(startDate, endDate)

	if (years === 0 && months === 0) {
		return '< 1m'
	}

	const parts: string[] = []
	if (years > 0) {
		parts.push(`${years}y`)
	}
	if (months > 0) {
		parts.push(`${months}m`)
	}

	return parts.join(' ')
}

export const experiences: ExperienceData[] = [
	{
		badgeKey: 'portfolio.section.experience.badge.leadDev',
		company: 'Orange Business',
		descriptionKey: 'portfolio.section.experience.description.liveIntelligence',
		endDate: null, // Present
		externalLink:
			'https://www.orange-business.com/fr/solutions/data-ia-iot/live-intelligence',
		highlightKeys: [
			'portfolio.section.experience.highlight.liveIntelligence.h1',
			'portfolio.section.experience.highlight.liveIntelligence.h2',
			'portfolio.section.experience.highlight.liveIntelligence.h3',
			'portfolio.section.experience.highlight.liveIntelligence.h4',
			'portfolio.section.experience.highlight.liveIntelligence.h5',
		],
		id: 'live-intelligence',
		location: 'Bordeaux, France',
		periodKey: 'portfolio.section.experience.period.liveIntelligence',
		projectKey: 'portfolio.section.experience.project.liveIntelligence',
		roleKey: 'portfolio.section.experience.role.fullstack',
		startDate: new Date(2025, 0), // Jan 2025
		tech: [
			{ category: 'frontend', name: 'React' },
			{ category: 'frontend', name: 'TypeScript' },
			{ category: 'frontend', name: 'TanStack' },
			{ category: 'frontend', name: 'Zod' },
			{ category: 'frontend', name: 'BiomeJs' },
			{ category: 'backend', name: '.NET' },
			{ category: 'backend', name: 'ASP.NET' },
			{ category: 'backend', name: 'EF Core' },
			{ category: 'data', name: 'PostgreSQL' },
			{ category: 'data', name: 'Keycloak' },
			{ category: 'infra', name: 'Kubernetes' },
			{ category: 'infra', name: 'FluxCD' },
			{ category: 'infra', name: 'ArgoCD' },
		],
	},
	{
		company: 'Orange Business',
		descriptionKey: 'portfolio.section.experience.description.packIdMigration',
		endDate: new Date(2025, 0), // Jan 2025
		externalLink:
			'https://www.orange-business.com/fr/solutions/mobilite/pack-id',
		highlightKeys: [
			'portfolio.section.experience.highlight.packIdMigration.h1',
			'portfolio.section.experience.highlight.packIdMigration.h2',
			'portfolio.section.experience.highlight.packIdMigration.h3',
			'portfolio.section.experience.highlight.packIdMigration.h4',
		],
		id: 'pack-id-migration',
		location: 'Bordeaux, France',
		periodKey: 'portfolio.section.experience.period.packIdMigration',
		projectKey: 'portfolio.section.experience.project.packIdMigration',
		roleKey: 'portfolio.section.experience.role.fullstack',
		startDate: new Date(2023, 11), // Dec 2023
		tech: [
			{ category: 'frontend', name: 'React' },
			{ category: 'backend', name: 'Kotlin' },
			{ category: 'backend', name: 'Spring Boot 3' },
			{ category: 'data', name: 'MariaDb' },
			{ category: 'data', name: 'Redis' },
			{ category: 'infra', name: 'Azure' },
			{ category: 'infra', name: 'Kubernetes' },
			{ category: 'infra', name: 'Helm' },
			{ category: 'migration', name: 'Java → Kotlin' },
			{ category: 'migration', name: 'VM → Cloud' },
		],
	},
	{
		company: 'Orange Business',
		descriptionKey: 'portfolio.section.experience.description.packIdApprentice',
		endDate: new Date(2021, 10), // Nov 2021
		externalLink:
			'https://www.orange-business.com/fr/solutions/mobilite/pack-id',
		highlightKeys: [
			'portfolio.section.experience.highlight.packIdApprentice.h1',
			'portfolio.section.experience.highlight.packIdApprentice.h2',
			'portfolio.section.experience.highlight.packIdApprentice.h3',
		],
		id: 'pack-id-apprentice',
		location: 'Bordeaux, France',
		periodKey: 'portfolio.section.experience.period.packIdApprentice',
		projectKey: 'portfolio.section.experience.project.packId',
		roleKey: 'portfolio.section.experience.role.apprentice',
		startDate: new Date(2019, 9), // Oct 2019
		tech: [
			{ category: 'frontend', name: 'Jtwig' },
			{ category: 'backend', name: 'Java' },
			{ category: 'backend', name: 'Kotlin' },
			{ category: 'backend', name: 'Spring Boot' },
			{ category: 'data', name: 'MariaDb' },
			{ category: 'mobile', name: 'Android' },
		],
	},
]
